import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import { formatCurrency, formatDate, clearEmailVerification } from '../utils/helpers'
import './CartSummary.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faUserCheck, faUser } from '@fortawesome/free-solid-svg-icons'

const CartSummary = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [orderData, setOrderData] = useState(null)
  const [memberInfo, setMemberInfo] = useState(null)
  const [memberCheckComplete, setMemberCheckComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  useEffect(() => {
    // Check if we have order data and email verification
    const storedOrder = sessionStorage.getItem('ticketOrder')
    if (!storedOrder) {
      navigate('/')
      return
    }
    
    const order = JSON.parse(storedOrder)
    if (!order.emailVerified) {
      navigate('/verify-email')
      return
    }
    
    setOrderData(order)

    // Check if user is a member
    if (order.email) {
      graphqlAPI.getMemberByEmail(order.email)
        .then(member => {
          if (member) setMemberInfo(member)
        })
        .catch(console.error)
        .finally(() => setMemberCheckComplete(true))
    } else {
      setMemberCheckComplete(true)
    }
  }, [navigate])

  const handleProceedToPayment = async () => {
    if (!acceptedTerms) {
      setError(t('cart.acceptTermsError'))
      return
    }

    try {
      setLoading(true)
      setError('')
      
      // Create the order in the backend
      const orderResponse = await graphqlAPI.createTicketOrder(
        orderData.eventId,
        orderData.email,
        orderData.quantity,
        orderData.pricePerTicket,
        orderData.total
      )

      // Store order ID for payment process
      const updatedOrder = { 
        ...orderData, 
        orderId: orderResponse.orderId,
        orderToken: orderResponse.token
      }
      sessionStorage.setItem('ticketOrder', JSON.stringify(updatedOrder))
      
      // Redirect to payment
      navigate('/payment')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEditOrder = () => {
    // Go back to ticket selection
    navigate(`/tickets/${orderData.eventId}`)
  }

  const handleChangeEmail = () => {
    // Clear email verification session and go back to email verification
    clearEmailVerification()
    const updatedOrder = { ...orderData }
    delete updatedOrder.emailVerified
    sessionStorage.setItem('ticketOrder', JSON.stringify(updatedOrder))
    navigate('/verify-email')
  }

  if (!orderData) {
    return (
      <div className="cart-summary">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-3">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="cart-summary">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Progress Indicator */}
            <div className="progress-indicator mb-4">
              <div className="step completed">
                <div className="step-number">1</div>
                <span>{t('cart.stepTickets')}</span>
              </div>
              <div className="step completed">
                <div className="step-number">2</div>
                <span>{t('cart.stepEmail')}</span>
              </div>
              <div className="step active">
                <div className="step-number">3</div>
                <span>{t('cart.stepPayment')}</span>
              </div>
            </div>

            <div className="cart-card card">
              <div className="card-body">
                <h2 className="card-title text-center mb-4">
                  {t('cart.title')}
                </h2>

                {/* Order Details */}
                <div className="order-details mb-4">
                  <h4>{t('cart.orderDetails')}</h4>
                  
                  <div className="event-info">
                    <h5 className="event-name">{orderData.eventName}</h5>
                    
                    <div className="detail-row">
                      <span className="label">{t('cart.email')}:</span>
                      <span className="value">
                        {orderData.email}
                        <button
                          type="button"
                          className="btn btn-link btn-sm ms-2"
                          onClick={handleChangeEmail}
                        >
                          {t('cart.change')}
                        </button>
                      </span>
                    </div>
                    
                    <div className="detail-row">
                      <span className="label">{t('cart.quantity')}:</span>
                      <span className="value">
                        {orderData.quantity} {orderData.quantity === 1 ? 'entrada' : 'entradas'}
                        <button
                          type="button"
                          className="btn btn-link btn-sm ms-2"
                          onClick={handleEditOrder}
                        >
                          {t('cart.change')}
                        </button>
                      </span>
                    </div>
                    
                    <div className="detail-row">
                      <span className="label">{t('cart.priceType')}:</span>
                      <span className="value">
                        {orderData.priceType === 'early' ? t('cart.earlyBird') : t('cart.regular')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="price-breakdown mb-4">
                  <h4>{t('cart.priceBreakdown')}</h4>
                  
                  <div className="breakdown-item">
                    <span>{orderData.quantity} × {formatCurrency(orderData.pricePerTicket)}</span>
                    <span>{formatCurrency(orderData.quantity * orderData.pricePerTicket)}</span>
                  </div>
                  
                  <div className="breakdown-item total">
                    <span>{t('cart.total')}</span>
                    <span className="total-amount">{formatCurrency(orderData.total)}</span>
                  </div>
                </div>

              {/* Member Status */}
              {memberCheckComplete && (
                <div className={`alert ${memberInfo && memberInfo.category !== 'No Socio' ? 'alert-success' : 'alert-light border'} mb-4`}>
                  <div className="d-flex align-items-center returning-info">
                    <FontAwesomeIcon 
                      icon={memberInfo && memberInfo.category !== 'No Socio' ? faUserCheck : faUser} 
                      className={`me-3 fa-lg ${memberInfo && memberInfo.category !== 'No Socio' ? '' : 'text-secondary'}`} 
                    />
                    <div>
                      {memberInfo && memberInfo.category !== 'No Socio' ? (
                        <>
                          <p className="alert-heading mb-1 h6">{t('cart.welcomeMember', { name: memberInfo.name })}</p>
                          <p className="mb-0 small">
                            {t('cart.memberCategory', { category: memberInfo.category })}
                          </p>
                        </>
                      ) : (
                        <p className="mb-0 small text-secondary">{t('cart.notMemberMessage')}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Important Information */}
              <div className="important-info mb-4">
                  <h5><FontAwesomeIcon icon={faInfoCircle} /> {t('cart.importantInfo')}</h5>
                  <ul>
                    <li>{t('cart.ticketDelivery')}</li>
                    <li>{t('cart.refundPolicy')}</li>
                    <li>{t('cart.eventChanges')}</li>
                  </ul>
                </div>

                {/* Terms and Conditions */}
                <div className="terms-section mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      className="form-check-input"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                    />
                    <label htmlFor="acceptTerms" className="form-check-label">
                      {t('cart.acceptTerms')}{' '}
                      <a href="/#/terms" target="_blank" rel="noopener noreferrer">
                        {t('cart.termsLink')}
                      </a>{' '}
                      {t('cart.and')}{' '}
                      <a href="/#/privacy" target="_blank" rel="noopener noreferrer">
                        {t('cart.privacyLink')}
                      </a>
                    </label>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger mb-4">
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="form-actions">
                  <Link className="back-link left me-3"
                    onClick={() => navigate(-1)}
                    disabled={loading}
                  >
                    {t('common.back')}
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleProceedToPayment}
                    disabled={loading || !acceptedTerms}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-sm me-2"></span>
                        {t('cart.processing')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-credit-card me-2"></i>
                        {t('cart.proceedToPayment')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CartSummary